/**
 * CountryConfirmBanner - One-time banner asking user to confirm their detected region.
 * Shows after login when identity.countryConfirmed is falsy.
 * Auto-dismisses after 15 seconds.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRegionConfig } from '../../config/affiliateConfig';
import type { SupportedRegion } from '../../config/affiliateConfig';

interface CountryConfirmBannerProps {
  detectedRegion: SupportedRegion;
  language: string;
  uid?: string;
  onConfirm: (region: SupportedRegion) => Promise<void>;
  onChangeClick: () => void;
}

const AUTO_DISMISS_MS = 15_000;

export default function CountryConfirmBanner({
  detectedRegion,
  language,
  uid,
  onConfirm,
  onChangeClick,
}: CountryConfirmBannerProps) {
  const { t } = useTranslation('common');
  const storageKey = uid ? `countryConfirmDismissed_${uid}` : 'countryConfirmDismissed';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync dismissed state when uid changes (e.g., auth init: undefined → defined)
  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(storageKey) === 'true');
    } catch { /* storage blocked */ }
  }, [storageKey]);

  useEffect(() => {
    if (dismissed) return;
    timerRef.current = setTimeout(() => {
      // Auto-confirm detected region on timeout
      onConfirm(detectedRegion).then(() => {
        setDismissed(true);
        try { localStorage.setItem(storageKey, 'true'); } catch { /* */ }
      }).catch(() => {});
    }, AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [detectedRegion, dismissed, onConfirm]);

  if (dismissed) return null;

  const rc = getRegionConfig(detectedRegion);
  const regionName = language === 'ko' ? rc.labelKo : rc.label;

  return (
    <div className="country-confirm-banner">
      <span className="country-confirm-text">
        {t('countryConfirm.detected', {
          flag: rc.flag,
          region: regionName,
          currency: rc.currency,
          defaultValue: `${rc.flag} We detected your region as **${regionName}** · ${rc.currency}`,
        }).split('**').map((part, i) =>
          i % 2 === 1 ? <strong key={i}>{part}</strong> : part
        )}
      </span>
      <span className="country-confirm-actions">
        <button
          className="country-confirm-btn country-confirm-btn--ok"
          onClick={async () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            try {
              await onConfirm(detectedRegion);
              setDismissed(true);
              try { localStorage.setItem(storageKey, 'true'); } catch { /* */ }
            } catch { /* keep banner visible so user can retry */ }
          }}
        >
          {t('countryConfirm.correct', { defaultValue: '✓ Correct' })}
        </button>
        <button
          className="country-confirm-btn country-confirm-btn--change"
          onClick={() => {
            if (timerRef.current) clearTimeout(timerRef.current);
            onChangeClick();
            setDismissed(true);
            // Don't persist to localStorage — country isn't confirmed yet.
            // Banner will reappear next session if user didn't complete selection.
          }}
        >
          {t('countryConfirm.change', { defaultValue: 'Change →' })}
        </button>
      </span>
    </div>
  );
}
